import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { get, post, API } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import CashflowSummaryDrillDown from '../components/CashflowSummaryDrillDown';
import { fmtBRL, fmtBRLCompact, fmtDate, competenceLabel, competenceShort } from '../lib/format';
import { ArrowDownToLine, ArrowUpFromLine, Wallet, Activity, Calendar, TrendingUp, AlertCircle, Pencil, Save, Upload, Info, FileDown, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const TooltipDark = ({ active, payload, label, asDate = true }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-md p-3 shadow-xl text-[12px]">
      <div className="text-zinc-400 mb-1.5">{asDate ? fmtDate(label) : label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-zinc-300">{p.name}:</span>
          <span className="font-mono text-zinc-50">{fmtBRL(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// Componente: aba "Por Período" com seletor livre de datas
function PeriodoLivreTab({ companyFilter }) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      const params = { date_from: dateFrom, date_to: dateTo };
      if (cid) params.company_id = cid;
      const r = await get('/cashflow/range', params);
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar período');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo, companyFilter]);

  const shortcuts = [
    {
      label: 'Hoje',
      apply: () => {
        const d = new Date().toISOString().slice(0, 10);
        setDateFrom(d); setDateTo(d);
      },
    },
    {
      label: 'Esta Semana',
      apply: () => {
        const now = new Date();
        const day = now.getDay(); // 0=domingo
        const start = new Date(now); start.setDate(now.getDate() - day);
        setDateFrom(start.toISOString().slice(0, 10));
        setDateTo(now.toISOString().slice(0, 10));
      },
    },
    {
      label: 'Este Mês',
      apply: () => {
        const now = new Date();
        setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
        setDateTo(now.toISOString().slice(0, 10));
      },
    },
    {
      label: 'Mês Anterior',
      apply: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        setDateFrom(start.toISOString().slice(0, 10));
        setDateTo(end.toISOString().slice(0, 10));
      },
    },
    {
      label: 'Últimos 30 dias',
      apply: () => {
        const now = new Date();
        const start = new Date(now); start.setDate(now.getDate() - 30);
        setDateFrom(start.toISOString().slice(0, 10));
        setDateTo(now.toISOString().slice(0, 10));
      },
    },
  ];

  const totals = data?.totals || { entradas: 0, saidas: 0, liquido: 0, lancamentos_count: 0 };
  const flow = data?.flow || [];

  return (
    <div className="space-y-5">
      {/* Seletor */}
      <div className="ceap-card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Inicial</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-zinc-900 border-zinc-700 mt-1"
              data-testid="period-date-from"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Final</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-zinc-900 border-zinc-700 mt-1"
              data-testid="period-date-to"
            />
          </div>
          <div className="flex flex-wrap gap-1 ml-auto">
            {shortcuts.map((s) => (
              <Button
                key={s.label}
                variant="outline"
                size="sm"
                onClick={s.apply}
                className="border-zinc-700 text-zinc-400 hover:text-zinc-100 text-xs"
                data-testid={`period-shortcut-${s.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {s.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard label="Entradas no Período" value={totals.entradas} accent="#10B981" icon={ArrowDownToLine} testid="period-kpi-entradas" />
        <KpiCard label="Saídas no Período" value={totals.saidas} accent="#EF4444" icon={ArrowUpFromLine} testid="period-kpi-saidas" />
        <KpiCard label="Saldo Líquido" value={totals.liquido} accent={totals.liquido >= 0 ? '#10B981' : '#EF4444'} icon={Wallet} testid="period-kpi-liquido" />
        <KpiCard label="Lançamentos" value={totals.lancamentos_count} format="int" accent="#D4AF37" icon={Activity} testid="period-kpi-count" />
      </div>

      {/* Gráfico */}
      <SectionCard title={`De ${fmtDate(dateFrom)} até ${fmtDate(dateTo)}`}>
        {loading ? (
          <div className="py-10 text-center text-zinc-500 text-sm">Carregando...</div>
        ) : flow.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm">Sem lançamentos no período</div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={flow} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#52525B" fontSize={10} tickFormatter={(d) => d.slice(-5)} />
                <YAxis stroke="#52525B" fontSize={10} tickFormatter={fmtBRLCompact} />
                <Tooltip content={<TooltipDark />} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#A1A1AA' }} />
                <Bar dataKey="entrada" name="Entrada" fill="#10B981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="saida" name="Saída" fill="#EF4444" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="saldo" name="Saldo Acumulado" stroke="#D4AF37" strokeWidth={2.5} dot={{ r: 3, fill: '#D4AF37' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      {/* Tabela diária */}
      {flow.length > 0 && (
        <SectionCard padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full ceap-table" data-testid="period-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th className="text-right">Entradas</th>
                  <th className="text-right">Saídas</th>
                  <th className="text-right">Líquido</th>
                  <th className="text-right">Saldo Acum.</th>
                </tr>
              </thead>
              <tbody>
                {flow.map((d) => (
                  <tr key={d.date} data-testid={`period-row-${d.date}`}>
                    <td className="font-mono text-zinc-300">{fmtDate(d.date)}</td>
                    <td className="text-right font-mono emerald-text">{fmtBRL(d.entrada)}</td>
                    <td className="text-right font-mono red-text">{fmtBRL(d.saida)}</td>
                    <td className={`text-right font-mono ${d.entrada - d.saida >= 0 ? 'emerald-text' : 'red-text'}`}>
                      {fmtBRL(d.entrada - d.saida)}
                    </td>
                    <td className={`text-right font-mono font-semibold ${d.saldo >= 0 ? 'gold-text' : 'red-text'}`}>
                      {fmtBRL(d.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export default function FluxoCaixa() {
  const { competence, companyFilter } = useApp();
  const [flow, setFlow] = useState([]);
  const [projected, setProjected] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editBalanceOpen, setEditBalanceOpen] = useState(false);
  const [balanceForm, setBalanceForm] = useState({ opening_balance: '', opening_date: '' });
  const [reportBusy, setReportBusy] = useState(false);

  const loadAll = async () => {
    const cid = companyFilter === 'all' ? null : companyFilter;
    const [f, pj, up, sm] = await Promise.all([
      get('/dashboard/cashflow', { competence, ...(cid ? { company_id: cid } : {}) }),
      get('/cashflow/projected', { competence, ...(cid ? { company_id: cid } : {}) }),
      get('/cashflow/upcoming', { months: 6, ...(cid ? { company_id: cid } : {}) }),
      get('/cashflow/summary', { competence, ...(cid ? { company_id: cid } : {}) }),
    ]);
    setFlow(f);
    setProjected(pj);
    setUpcoming(up);
    setSummary(sm);
  };

  useEffect(() => { loadAll(); }, [competence, companyFilter]);

  const openEditBalance = () => {
    setBalanceForm({
      opening_balance: summary?.opening_balance ? String(summary.opening_balance) : '',
      opening_date: summary?.opening_date || `01/${competence.split('-')[1]}/${competence.split('-')[0]}`,
    });
    setEditBalanceOpen(true);
  };

  const saveBalance = async () => {
    const value = parseFloat(String(balanceForm.opening_balance).replace(',', '.'));
    if (isNaN(value)) return toast.error('Valor inválido');
    try {
      await post('/cash-balances', {
        company_id: companyFilter === 'all' ? null : companyFilter,
        competence,
        opening_balance: value,
        opening_date: balanceForm.opening_date,
      });
      toast.success('Saldo de caixa salvo');
      setEditBalanceOpen(false);
      loadAll();
    } catch (e) {
      toast.error('Erro ao salvar saldo');
    }
  };

  const downloadReport = async () => {
    setReportBusy(true);
    try {
      const cid = companyFilter === 'all' ? null : companyFilter;
      const params = { competence };
      if (cid) params.company_id = cid;
      const res = await axios.get(`${API}/reports/cashflow-pdf`, {
        params,
        responseType: 'blob',
        withCredentials: true,
        timeout: 120000,
      });
      const disp = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match ? match[1] : `relatorio-fluxo-caixa_${competence}.pdf`;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório gerado');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erro ao gerar relatório';
      toast.error(detail);
    } finally {
      setReportBusy(false);
    }
  };

  const totalIn = flow.reduce((acc, d) => acc + d.entrada, 0);
  const totalOut = flow.reduce((acc, d) => acc + d.saida, 0);
  const saldoFinal = flow.length ? flow[flow.length - 1].saldo : 0;
  const hasAnyData = totalIn > 0 || totalOut > 0 || (summary && (summary.totals?.entradas?.realizado > 0 || summary.totals?.saidas?.realizado > 0 || summary.totals?.entradas?.previsao > 0 || summary.totals?.saidas?.previsao > 0));
  const [summaryDrill, setSummaryDrill] = useState(null);
  const [listEntries, setListEntries] = useState([]);

  // Carregar lançamentos do mês (com descrição) - para tabela do tab Realizado
  useEffect(() => {
    if (!competence) return;
    const cid = companyFilter === 'all' ? null : companyFilter;
    get('/entries', {
      competence,
      ...(cid ? { company_id: cid } : {}),
    }).then((r) => setListEntries(Array.isArray(r) ? r : [])).catch(() => setListEntries([]));
  }, [competence, companyFilter]);

  return (
    <div className="fade-in">
      <TopBar
        title="Fluxo de Caixa"
        subtitle="Realizado, provisionado e projeção futura"
        action={
          <Button
            onClick={downloadReport}
            disabled={reportBusy}
            className="btn-gold rounded-md gap-2 px-4"
            data-testid="btn-gerar-relatorio-fluxo"
          >
            {reportBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {reportBusy ? 'Gerando PDF…' : 'Gerar Relatório'}
          </Button>
        }
      />
      <div className="px-8 py-7 space-y-5">
        {!hasAnyData && (
          <div className="flex items-center gap-4 px-5 py-4 bg-amber-950/20 border border-amber-700/40 rounded-md" data-testid="fluxo-empty-banner">
            <Info size={18} className="text-amber-400 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-100">
                Sem dados de fluxo para {competenceLabel(competence)}
              </div>
              <div className="text-[12px] text-amber-100/70 mt-0.5">
                Faça o upload da planilha desta empresa em "Importar Fluxo de Caixa" para preencher o Resumo Geral, o detalhamento diário e a projeção.
              </div>
            </div>
            <Link to="/importar-fluxo-caixa">
              <Button className="btn-gold gap-1.5" size="sm" data-testid="btn-fluxo-import">
                <Upload size={13} /> Importar Planilha
              </Button>
            </Link>
          </div>
        )}
        <Tabs defaultValue="realizado">
          <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1" data-testid="cashflow-tabs">
            <TabsTrigger value="realizado" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 text-xs px-4" data-testid="tab-realizado">
              <Calendar size={13} className="mr-2" /> Realizado (diário)
            </TabsTrigger>
            <TabsTrigger value="provisionado" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 text-xs px-4" data-testid="tab-provisionado">
              <TrendingUp size={13} className="mr-2" /> Provisionado (mês corrente + próximo)
            </TabsTrigger>
            <TabsTrigger value="periodo" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 text-xs px-4" data-testid="tab-periodo">
              <Calendar size={13} className="mr-2" /> Por Período
            </TabsTrigger>
          </TabsList>

          {/* TAB REALIZADO */}
          <TabsContent value="realizado" className="space-y-5 mt-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard label="Entradas no Período" value={totalIn} accent="#10B981" icon={ArrowDownToLine} testid="cf-entradas" />
              <KpiCard label="Saídas no Período" value={totalOut} accent="#EF4444" icon={ArrowUpFromLine} testid="cf-saidas" />
              <KpiCard label="Saldo Líquido" value={totalIn - totalOut} accent={totalIn - totalOut >= 0 ? '#10B981' : '#EF4444'} icon={Wallet} testid="cf-saldo" />
              <KpiCard label="Saldo Acumulado" value={saldoFinal} accent="#D4AF37" icon={Activity} testid="cf-acumulado" highlight />
            </div>
            <SectionCard title="Evolução Diária" subtitle="Entradas, saídas e saldo acumulado">
              {flow.length === 0 ? (
                <div className="py-10 text-center text-zinc-500 text-sm">Sem lançamentos no período</div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={flow} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" stroke="#52525B" fontSize={10} tickFormatter={(d) => d.slice(-2)} />
                      <YAxis stroke="#52525B" fontSize={10} tickFormatter={fmtBRLCompact} />
                      <Tooltip content={<TooltipDark />} />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#A1A1AA' }} />
                      <Bar dataKey="entrada" name="Entrada" fill="#10B981" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="saida" name="Saída" fill="#EF4444" radius={[3, 3, 0, 0]} />
                      <Line type="monotone" dataKey="saldo" name="Saldo Acumulado" stroke="#D4AF37" strokeWidth={2.5} dot={{ r: 3, fill: '#D4AF37' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </SectionCard>
            <SectionCard title="Detalhamento Diário" padded={false}>
              <div className="overflow-x-auto">
                <table className="w-full ceap-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th className="text-right">Entradas</th>
                      <th className="text-right">Saídas</th>
                      <th className="text-right">Líquido</th>
                      <th className="text-right">Saldo Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flow.map((d) => (
                      <tr key={d.date} data-testid={`cf-row-${d.date}`}>
                        <td className="font-mono text-zinc-300">{fmtDate(d.date)}</td>
                        <td className="text-right font-mono emerald-text">{fmtBRL(d.entrada)}</td>
                        <td className="text-right font-mono red-text">{fmtBRL(d.saida)}</td>
                        <td className={`text-right font-mono ${d.entrada - d.saida >= 0 ? 'emerald-text' : 'red-text'}`}>
                          {fmtBRL(d.entrada - d.saida)}
                        </td>
                        <td className={`text-right font-mono font-semibold ${d.saldo >= 0 ? 'gold-text' : 'red-text'}`}>
                          {fmtBRL(d.saldo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard
              title="Lançamentos do Mês (descrição linha-a-linha)"
              subtitle="Lista completa de entradas e saídas com descrição — clique numa linha para identificar o que entrou/saiu"
              padded={false}
              testid="entries-list"
            >
              <div className="overflow-x-auto" style={{ maxHeight: '480px' }}>
                <table className="w-full ceap-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th>Tipo</th>
                      <th className="text-center">Status</th>
                      <th className="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listEntries.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-zinc-500 py-8">Sem lançamentos nesta competência</td></tr>
                    )}
                    {listEntries.map((e) => {
                      const isIn = e.type === 'RECEITA';
                      return (
                        <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                          <td className="font-mono text-xs text-zinc-400">{fmtDate(e.date)}</td>
                          <td className="text-zinc-100">{e.description}</td>
                          <td className="text-[11px] text-zinc-400">{e.type}</td>
                          <td className="text-center">
                            <span className={`pill text-[10px] ${e.status === 'PAGO' || e.status === 'CONCILIADO' ? 'pill-success' : 'pill-warning'}`}>{e.status}</span>
                          </td>
                          <td className={`text-right font-mono ${isIn ? 'emerald-text' : 'red-text'}`}>
                            {isIn ? '+' : '−'} {fmtBRL(e.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </TabsContent>
          {/* TAB PROVISIONADO - RESUMO GERAL como planilha original */}
          <TabsContent value="provisionado" className="space-y-5 mt-5">
            {summary && (
              <SectionCard
                title={`Resumo Geral - ${competenceLabel(competence)}`}
                subtitle="Replica a estrutura da planilha tradicional: comparativo lado a lado de Previsão × Realizado, com saldo de caixa e acumulado projetado"
                action={
                  <Button
                    onClick={openEditBalance}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 gap-1.5"
                    size="sm"
                    data-testid="btn-edit-balance"
                  >
                    <Pencil size={13} /> {summary.has_opening_balance ? 'Editar Saldo Inicial' : 'Definir Saldo Inicial'}
                  </Button>
                }
              >
                <div className="overflow-x-auto">
                  <table className="w-full ceap-table" data-testid="resumo-geral-table">
                    <thead>
                      <tr>
                        <th className="w-10 text-center">#</th>
                        <th>Descrição</th>
                        <th className="text-right">Previsão</th>
                        <th className="text-right">Realizado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr data-testid="row-total-saidas">
                        <td className="text-center text-zinc-500 font-mono">1</td>
                        <td className="text-zinc-100 font-medium">Total Saídas</td>
                        <td className="text-right font-mono text-amber-300 cursor-pointer hover:bg-zinc-900/60" data-testid="saidas-previsao" onClick={() => setSummaryDrill({ key: 'saidas', col: 'previsao', label: 'Saídas Previstas (Provisionadas)' })}>{fmtBRL(summary.totals.saidas.previsao)}</td>
                        <td className="text-right font-mono red-text cursor-pointer hover:bg-zinc-900/60" data-testid="saidas-realizado" onClick={() => setSummaryDrill({ key: 'saidas', col: 'realizado', label: 'Saídas Realizadas' })}>{fmtBRL(summary.totals.saidas.realizado)}</td>
                      </tr>
                      <tr data-testid="row-total-entradas">
                        <td className="text-center text-zinc-500 font-mono">2</td>
                        <td className="text-zinc-100 font-medium">Total Entradas</td>
                        <td className="text-right font-mono text-amber-300 cursor-pointer hover:bg-zinc-900/60" data-testid="entradas-previsao" onClick={() => setSummaryDrill({ key: 'entradas', col: 'previsao', label: 'Entradas Previstas (Provisionadas)' })}>{fmtBRL(summary.totals.entradas.previsao)}</td>
                        <td className="text-right font-mono emerald-text cursor-pointer hover:bg-zinc-900/60" data-testid="entradas-realizado" onClick={() => setSummaryDrill({ key: 'entradas', col: 'realizado', label: 'Entradas Realizadas' })}>{fmtBRL(summary.totals.entradas.realizado)}</td>
                      </tr>
                      <tr className="bg-zinc-900/40" data-testid="row-caixa-inicial">
                        <td className="text-center text-zinc-500 font-mono">3</td>
                        <td className="text-zinc-100">
                          <strong className="gold-text">Caixa em {summary.opening_date}</strong>
                          {!summary.has_opening_balance && (
                            <span className="ml-2 pill pill-warning text-[10px]">
                              clique em "Definir Saldo Inicial"
                            </span>
                          )}
                        </td>
                        <td className="text-right font-mono font-semibold gold-text text-base" data-testid="opening-balance-previsao">
                          {fmtBRL(summary.opening_balance)}
                        </td>
                        <td className="text-right font-mono font-semibold gold-text text-base" data-testid="opening-balance-realizado">
                          {fmtBRL(summary.opening_balance)}
                        </td>
                      </tr>
                      <tr className="bg-amber-950/20 border-t-2 border-amber-700/40" data-testid="row-caixa-acumulado-curr">
                        <td className="text-center text-zinc-500 font-mono">4</td>
                        <td className="text-zinc-50">
                          <strong className="gold-text">Caixa Acumulado em {summary.next_date_label}</strong>
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            = Saldo Inicial + Entradas − Saídas (por coluna)
                            {!summary.has_opening_balance && (
                              <span className="ml-1 text-amber-400/80">— defina o Saldo Inicial para ver o saldo real</span>
                            )}
                          </div>
                        </td>
                        <td className={`text-right font-mono font-bold text-lg ${summary.balance_end_current.previsao >= 0 ? 'emerald-text' : 'red-text'}`} data-testid="balance-end-current-previsao">
                          {fmtBRL(summary.balance_end_current.previsao)}
                        </td>
                        <td className={`text-right font-mono font-bold text-lg ${summary.balance_end_current.realizado >= 0 ? 'emerald-text' : 'red-text'}`} data-testid="balance-end-current-realizado">
                          {fmtBRL(summary.balance_end_current.realizado)}
                        </td>
                      </tr>
                      {/* Item 3: Linha 5 removida por solicitação do usuário
                          (mantemos apenas o Caixa Acumulado do mês corrente) */}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}

            {projected && (
              <SectionCard title="Detalhamento Adicional" subtitle={`Quebra entre realizado de ${competenceLabel(projected.competence_realized)} e provisionado de ${competenceLabel(projected.competence_forecast)}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border border-zinc-800 rounded-lg p-5 bg-zinc-900/40">
                    <div className="overline gold-text mb-3">📅 Realizado - {competenceLabel(projected.competence_realized)}</div>
                    <div className="space-y-3">
                      <div className="flex justify-between"><span className="text-sm text-zinc-300">Entradas Realizadas</span><span className="font-mono emerald-text font-semibold">{fmtBRL(projected.realized.entradas)}</span></div>
                      <div className="flex justify-between"><span className="text-sm text-zinc-300">Saídas Realizadas</span><span className="font-mono red-text font-semibold">{fmtBRL(projected.realized.saidas)}</span></div>
                      <div className="border-t border-zinc-800 pt-3 flex justify-between">
                        <span className="text-sm font-medium text-zinc-100">Saldo Realizado</span>
                        <span className={`font-mono text-lg font-bold ${projected.realized.saldo >= 0 ? 'emerald-text' : 'red-text'}`}>{fmtBRL(projected.realized.saldo)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="border border-amber-700/40 rounded-lg p-5 bg-amber-950/10">
                    <div className="overline text-amber-400 mb-3">🔮 Provisionado - {competenceLabel(projected.competence_forecast)}</div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <div>
                          <div className="text-sm text-zinc-300">Entradas Previstas</div>
                          <div className="text-[11px] text-zinc-500">{projected.forecast_next_month.items_in} contas a receber</div>
                        </div>
                        <span className="font-mono emerald-text font-semibold">{fmtBRL(projected.forecast_next_month.entradas)}</span>
                      </div>
                      <div className="flex justify-between">
                        <div>
                          <div className="text-sm text-zinc-300">Saídas Previstas</div>
                          <div className="text-[11px] text-zinc-500">{projected.forecast_next_month.items_out} contas a pagar</div>
                        </div>
                        <span className="font-mono red-text font-semibold">{fmtBRL(projected.forecast_next_month.saidas)}</span>
                      </div>
                      <div className="border-t border-amber-700/30 pt-3 flex justify-between">
                        <span className="text-sm font-medium text-zinc-100">Saldo Previsto</span>
                        <span className={`font-mono text-lg font-bold ${projected.forecast_next_month.saldo >= 0 ? 'emerald-text' : 'red-text'}`}>{fmtBRL(projected.forecast_next_month.saldo)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}
          </TabsContent>

          {/* TAB PERÍODO LIVRE */}
          <TabsContent value="periodo" className="space-y-5 mt-5">
            <PeriodoLivreTab companyFilter={companyFilter} />
          </TabsContent>
        </Tabs>
      </div>

      {/* DIALOG Saldo Inicial de Caixa */}
      <Dialog open={editBalanceOpen} onOpenChange={setEditBalanceOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="dialog-edit-balance">
          <DialogHeader>
            <DialogTitle className="font-display">Saldo Inicial de Caixa</DialogTitle>
            <div className="text-xs text-zinc-500 mt-1">
              Esse é o saldo de caixa no início do mês de <strong className="gold-text">{competenceLabel(competence)}</strong>.
              É a base para calcular o caixa acumulado projetado.
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data de Referência (DD/MM/AAAA)</Label>
              <Input
                value={balanceForm.opening_date}
                onChange={(e) => setBalanceForm({ ...balanceForm, opening_date: e.target.value })}
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono"
                placeholder="01/05/2026"
                data-testid="balance-date-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Saldo de Caixa (R$) *</Label>
              <Input
                value={balanceForm.opening_balance}
                onChange={(e) => setBalanceForm({ ...balanceForm, opening_balance: e.target.value })}
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono text-lg"
                placeholder="50000,00"
                data-testid="balance-value-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Use ponto ou vírgula como decimal. Aceita valor negativo se estiver com saldo devedor.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditBalanceOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={saveBalance} className="btn-gold gap-1.5" data-testid="btn-save-balance">
              <Save size={14} /> Salvar Saldo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CashflowSummaryDrillDown
        open={!!summaryDrill}
        onClose={() => setSummaryDrill(null)}
        drill={summaryDrill}
        competence={competence}
        companyId={companyFilter}
      />
    </div>
  );
}
